import Breadcrumbs from "@/components/design-system/breadcrumbs";
import { Button } from "@/components/design-system/button";
import ListHeader from "@/components/design-system/list-header";
import ListPagination from "@/components/design-system/list-pagination";
import {
  Table,
  TableColumn,
  type TableSortConfig,
} from "@/components/design-system/table";
import {
  assignClassToStudentsService,
  assignStudentService,
} from "@/service/teacher.service";
import { AppDispatch } from "@/store";
import { selectSchoolId } from "@/store/slices/authSlice";
import {
  fetchStudents,
  resetAllState,
  selectStudentLoading,
  selectStudents,
  selectTotalItems,
} from "@/store/slices/studentSlice";
import { StudentModel, type StudentListType } from "@/types/student.model";
import { resolveSingleSchoolId } from "@/modules/User-Management/utils/schoolContext";
import { errorToast, successToast } from "@/utils/constants";
import { Square, SquareCheck } from "lucide-react";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";

interface StudentDisplay extends StudentModel {
  select?: boolean;
  sno?: number;
}

const ITEMS_PER_PAGE = 10;

function AssignStudents() {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const students = useSelector(selectStudents);
  const loading = useSelector(selectStudentLoading);
  const totalItems = useSelector(selectTotalItems);

  const [searchParams] = useSearchParams();

  const teacherId = searchParams.get("teacherId");
  const assignType =
    searchParams.get("assignType") ??
    (searchParams.get("classId") ? "class" : "course");
  const grade = searchParams.get("grade");
  const gradeName = searchParams.get("gradeName");
  const classId = searchParams.get("classId");
  const className = searchParams.get("className");
  const schoolId = searchParams.get("schoolId");
  const schoolIdFromUser = useSelector(selectSchoolId);
  const effectiveSchoolId =
    resolveSingleSchoolId(schoolId, schoolIdFromUser) ?? "";
  const isClassAssign = assignType === "class";
  const listType: StudentListType = isClassAssign ? "class" : "course";

  const [searchValue, setSearchValue] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [fromDate, setFromDate] = useState<Date | undefined>(undefined);
  const [toDate, setToDate] = useState<Date | undefined>(undefined);
  const [selectedStudents, setSelectedStudents] = useState<string[]>([]);
  const [assignLoading, setAssignLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState<TableSortConfig | undefined>(
    undefined
  );

  // Map frontend column keys to backend API sorting keys
  const getSortField = (key: string): string => {
    const sortFieldMap: Record<string, string> = {
      name: "NME",
    };
    return sortFieldMap[key] || key;
  };

  const handleSort = (config: TableSortConfig | null) => {
    setSortConfig(config ?? undefined);
    if (currentPage !== 1) setCurrentPage(1);
  };

  useEffect(() => {
    if (searchValue && currentPage !== 1) {
      setCurrentPage(1);
      return;
    }

    const sortParams = sortConfig
      ? { sortBy: getSortField(sortConfig.key) }
      : {};

    dispatch(
      fetchStudents({
        page: currentPage,
        pageSize: ITEMS_PER_PAGE,
        name: searchValue,
        fromDate: fromDate,
        toDate: toDate,
        schoolId: effectiveSchoolId || undefined,
        type: listType,
        ...sortParams,
        ...(isClassAssign
          ? { classId: classId ?? undefined }
          : { gradeId: grade ?? undefined }),
      })
    );
  }, [
    currentPage,
    searchValue,
    fromDate,
    toDate,
    grade,
    classId,
    effectiveSchoolId,
    isClassAssign,
    listType,
    sortConfig,
    dispatch,
  ]);

  useEffect(() => {
    if (!isClassAssign && teacherId && grade && students.length > 0) {
      const matchingStudents = students.filter((student) => {
        if (Array.isArray(student.teachers)) {
          return student.teachers.some((teacher) => {
            if (typeof teacher === "object" && teacher !== null) {
              const teacherIdMatches = teacher.id === teacherId;
              const gradeMatches =
                Array.isArray(teacher.grades) &&
                teacher.grades.some(
                  (graderItem) =>
                    typeof graderItem === "object" &&
                    graderItem !== null &&
                    graderItem.id === grade
                );

              return teacherIdMatches && gradeMatches;
            }
            return teacher === teacherId;
          });
        }
        if (student.teacher) {
          return student.teacher === teacherId;
        }
        if (Array.isArray(student.teacher_ids)) {
          return student.teacher_ids.includes(teacherId);
        }
        return false;
      });

      const matchingIds = matchingStudents.map((student) => student.id);

      setSelectedStudents((prevSelected) => {
        const uniqueIds = Array.from(
          new Set([...prevSelected, ...matchingIds])
        );
        return uniqueIds;
      });
    }
  }, [students, teacherId, grade, isClassAssign]);

  useEffect(() => {
    return () => {
      dispatch(resetAllState());
    };
  }, [dispatch]);

  const handleDateRangeChange = (
    startDate: Date | undefined,
    endDate: Date | undefined
  ) => {
    setFromDate(startDate);
    setToDate(endDate);
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  };

  const handleSelectStudent = (studentId: string) => {
    setSelectedStudents((prev) => {
      if (prev.includes(studentId)) {
        return prev.filter((id) => id !== studentId);
      }
      return [...prev, studentId];
    });
  };

  const handleSelectAllStudents = (checked: boolean) => {
    if (checked) {
      const currentPageStudents = students
        .slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE)
        .map((student) => student.id);
      setSelectedStudents(currentPageStudents);
    } else {
      setSelectedStudents([]);
    }
  };

  const isAllSelected = () => {
    const currentPageStudents = students
      .slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE)
      .map((student) => student.id);

    return (
      currentPageStudents.length > 0 &&
      currentPageStudents.every((id) => selectedStudents.includes(id))
    );
  };

  const handleAssignStudents = async () => {
    if (isClassAssign && !effectiveSchoolId) {
      errorToast(
        "School required",
        "Select a school from the teacher list before assigning a class."
      );
      return;
    }

    setAssignLoading(true);

    try {
      if (isClassAssign) {
        const {
          data: { message },
        } = await assignClassToStudentsService({
          tch_id: teacherId || "",
          class_ids: [classId || ""],
          student_ids: selectedStudents,
          schoolId: effectiveSchoolId,
        });
        successToast(message || "Classes and students assigned successfully", "");
      } else {
        const {
          data: { message },
        } = await assignStudentService({
          tch_id: teacherId || "",
          student_ids: selectedStudents,
          grade_id: [grade || ""],
        });
        successToast(message || "Students assigned successfully", "");
      }

      navigateToBack();
    } catch (error) {
      console.log(error);
    } finally {
      setAssignLoading(false);
    }
  };

  const navigateToBack = () => {
    navigate(
      effectiveSchoolId
        ? `/user-management/teacher?schoolId=${effectiveSchoolId}`
        : "/user-management/teacher"
    );
  };

  const columns: TableColumn<StudentDisplay>[] = [
    {
      title: (
        <div className="flex items-center justify-center">
          <div
            className="h-6 w-6 cursor-pointer"
            onClick={() => handleSelectAllStudents(!isAllSelected())}
          >
            {isAllSelected() ? (
              <SquareCheck className="text-[#5C88C5]" />
            ) : (
              <Square className="text-[#BDBDBD]" />
            )}
          </div>
        </div>
      ),
      dataIndex: "select",
      key: "select",
      maxWidth: 56,
      minWidth: 56,
      render: (_: unknown, record: { id: string }) => (
        <div className="flex items-center justify-center">
          <div
            className="h-6 w-6 cursor-pointer"
            onClick={() => handleSelectStudent(record.id)}
          >
            {selectedStudents.includes(record.id) ? (
              <SquareCheck className="text-[#5C88C5]" />
            ) : (
              <Square className="text-[#BDBDBD]" />
            )}
          </div>
        </div>
      ),
    },
    {
      title: "S.No",
      dataIndex: "id",
      key: "id",
      maxWidth: 68,
      minWidth: 68,
      align: "center",
      render: (_, __, index) => {
        const sno = (currentPage - 1) * ITEMS_PER_PAGE + index + 1;
        return (
          <div className="text-center text-[16px] font-medium text-borderBlack text-[#1F1F1F]">
            {String(sno).padStart(2, "0")}
          </div>
        );
      },
    },
    {
      title: "Student Name",
      dataIndex: "name",
      key: "name",
      maxWidth: 583,
      minWidth: 583,
      sortable: true,
      sortKey: "name",
      render: (_, record) => (
        <div
          className="text-[16px] font-medium text-[#1F1F1F]"
          style={{
            overflowWrap: "anywhere",
            whiteSpace: "pre-wrap",
          }}
        >
          {record.name}
        </div>
      ),
    },
  ];

  return (
    <div className="flex h-full flex-col">
      <div>
        <Breadcrumbs
          title="Assign Students"
          onClick={() => navigateToBack()}
          bg="#fff"
          rightElement={
            <div>
              <span className="ml-2 text-[20px] font-semibold text-[#1F1F1F]">
                {isClassAssign ? `Class: ${className}` : `Grade: ${gradeName}`}
              </span>
            </div>
          }
        />
      </div>

      <div className="flex h-full flex-1 flex-col overflow-hidden rounded-[12px] bg-white px-4 sm:px-6 md:px-[32px] w-full">
        <ListHeader
          title="Student List"
          showSearchCount={selectedStudents.length}
          searchValue={searchValue}
          showDateFilter={false}
          onSearchChange={setSearchValue}
          onDateRangeChange={handleDateRangeChange}
          rightAction={
            <Button
              variant="primary"
              size="md"
              className="h-[48px] w-[161px] bg-[#5C88C5] text-white"
              disabled={assignLoading}
              loading={assignLoading}
              onClick={handleAssignStudents}
            >
              Update
            </Button>
          }
        />

        <div className="flex flex-1 flex-col overflow-x-auto rounded-md border border-[#EDEDED]">
          <div className="flex h-full flex-1 overflow-auto">
            <Table
              columns={columns}
              data={students}
              rowKey="id"
              showHeader={true}
              size="middle"
              sticky
              scroll={{ x: 1200 }}
              loading={loading}
              sortConfig={sortConfig}
              onSort={handleSort}
            />
          </div>
        </div>

        <ListPagination
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          totalItems={totalItems}
          itemsPerPage={ITEMS_PER_PAGE}
          itemsLabel="Students"
        />
      </div>
    </div>
  );
}

export default AssignStudents;
